import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Button, Table, Tag, Input } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { useRecoilValue } from 'recoil';
import moment from 'moment';
import {
  timezoneAtom
} from '../../../atoms/Atoms';
import api from '../../../api/api';
import { renderDate } from '../../utils/functions';

function Pos() {
  const { t } = useTranslation();
  const timezone = useRecoilValue(timezoneAtom);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLanes, setFilteredLanes] = useState([]);

  function getTagColor(version) {
    if (version && result?.latestVersion && version === result?.latestVersion) {
      return 'green';
    }
    if (version && result?.latestVersion && version !== result?.latestVersion) {
      return 'red';
    }
    return '';
  }

  function getPosLanes() {
    setLoading(true);
    api
      .get('pos/all')
      .then((response) => {
        setLoading(false);
        setResult(response.data.data);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  }

  function filterLanes() {
    if (result?.lanes) {
      if (searchTerm?.length === 0) {
        setFilteredLanes(result?.lanes);
      } else {
        const tempLanes = _.filter(result?.lanes, (l) => `${l.businessName} ${l.tag} ${l.lastReportedVersion} ${l.lastReportedDeviceManufacturer} ${l.lastReportedDeviceModel} ${l.lastReportedDeviceName}`.toLowerCase().includes(searchTerm.toLocaleLowerCase()));
        setFilteredLanes(tempLanes);
      }
    }
  }

  useEffect(() => {
    getPosLanes();
  }, []);

  useEffect(() => {
    filterLanes();
  }, [searchTerm, result?.lanes]);

  return (
    <>
      <PageHeader
        className="mb-4 px-0"
        title={t('pos_lanes')}
        extra={[
          <Button
            id="reload"
            type="primary"
            key="reload"
            size="small"
            onClick={() => getPosLanes()}
          >
            {t('reload')}
          </Button>
        ]}
      />
      <p>{t('latest_version')}: <strong>{result?.latestVersion}</strong></p>
      <Input
        value={searchTerm}
        placeholder={t('search')}
        className="mt-2"
        disabled={loading}
        onChange={(obj) => setSearchTerm(obj.target.value)}
      />
      <Table
        pagination={false}
        size="small"
        bordered
        rowKey="id"
        loading={loading}
        columns={[
          {
            title: t('business'),
            key: 'business',
            align: 'center',
            dataIndex: 'businessName',
            sorter: (a, b) => a?.businessName?.localeCompare(b?.businessName),
            className: 'text-xs',
            render: (row) => (
              <span>{row}</span>
            ),
          },
          {
            title: t('tag'),
            key: 'tag',
            align: 'center',
            dataIndex: 'tag',
            className: 'text-xs',
            sorter: (a, b) => a?.tag?.localeCompare(b?.tag),
            render: (row) => (
              <span>{row}</span>
            ),
          },
          {
            title: t('blocked'),
            key: 'blocked',
            align: 'center',
            className: 'text-xs',
            sorter: (a, b) => a?.isBlocked > b?.isBlocked,
            render: (row) => (
              <Tag color={row.isBlocked ? 'red' : 'green'}>{row.isBlocked ? t('yes') : t('no')}</Tag>
            ),
          },
          {
            title: t('pos_version'),
            key: 'lastReportedVersion',
            align: 'center',
            dataIndex: 'lastReportedVersion',
            sorter: (a, b) => a?.lastReportedVersion?.localeCompare(b?.lastReportedVersion),
            className: 'text-xs',
            render: (row) => (
              <Tag color={getTagColor(row)}>{row ?? '-'}</Tag>
            ),
          },
          {
            title: t('device_manufacturer'),
            key: 'lastReportedDeviceManufacturer',
            align: 'center',
            dataIndex: 'lastReportedDeviceManufacturer',
            sorter: (a, b) => a?.lastReportedDeviceManufacturer?.localeCompare(b?.lastReportedDeviceManufacturer),
            className: 'text-xs',
            render: (row) => (
              <span>{row ?? '-'}</span>
            ),
          },
          {
            title: t('device_model'),
            key: 'lastReportedDeviceModel',
            align: 'center',
            dataIndex: 'lastReportedDeviceModel',
            sorter: (a, b) => a?.lastReportedDeviceModel?.localeCompare(b?.lastReportedDeviceModel),
            className: 'text-xs',
            render: (row) => (
              <span>{row ?? '-'}</span>
            ),
          },
          {
            title: t('device_name'),
            key: 'lastReportedDeviceName',
            align: 'center',
            dataIndex: 'lastReportedDeviceName',
            sorter: (a, b) => a?.lastReportedDeviceName?.localeCompare(b?.lastReportedDeviceName),
            className: 'text-xs',
            render: (row) => (
              <span>{row ?? '-'}</span>
            ),
          },
          {
            title: t('terminal_id'),
            key: 'lastReportedTerminalId',
            align: 'center',
            dataIndex: 'lastReportedTerminalId',
            sorter: (a, b) => a?.lastReportedTerminalId?.localeCompare(b?.lastReportedTerminalId),
            className: 'text-xs',
            render: (row) => (
              <span>{row ?? '-'}</span>
            ),
          },
          {
            title: t('last_transaction_date'),
            key: 'lastTransactionOn',
            align: 'center',
            dataIndex: 'lastTransactionOn',
            sorter: (a, b) => moment(a.lastTransactionOn) - moment(b.lastTransactionOn),
            className: 'text-xs',
            render: (row) => (
              <span>{row ? renderDate(row, timezone) : '-'}</span>
            ),
          }
        ]}
        dataSource={filteredLanes}
      />
    </>
  );
}
export default Pos;
